import { useState } from 'react';
import { Button, FormControlLabel, IconButton, Switch } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { JSONSchema7 } from 'json-schema';
import { RowStatus } from '../TableWithButtons/TableWithButtons';
import { AppSchemaForm, SchemaFormData } from './AppSchemaForm';
import { RawJsonEditor } from './RawJsonEditor';
import { useRawJsonDraft } from './useRawJsonDraft';

export type SchemaFormMode = 'form' | 'json';

export interface SchemaFormSectionProps {
    schema: JSONSchema7;
    values: SchemaFormData;
    onChange: (formData: SchemaFormData) => void;
    extraErrorsByPointer?: Record<string, string[]>;
    imagePreviewMap?: Record<string, string>;
    defaultCollapsed?: boolean;
    defaultMode?: SchemaFormMode;
    attrStatus?: RowStatus;
    onStatusChange?: (attrStatus: RowStatus) => void;
}

export const SchemaFormSection = ({
    schema,
    values,
    onChange,
    extraErrorsByPointer,
    imagePreviewMap,
    defaultCollapsed = false,
    defaultMode = 'form',
    attrStatus = 'ACTIVE',
    onStatusChange,
}: SchemaFormSectionProps) => {
    const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);
    const [mode, setMode] = useState<SchemaFormMode>(defaultMode);
    const draft = useRawJsonDraft(values, onChange, mode === 'json');

    const showForm = () => {
        if (draft.commit()) {
            setMode('form');
        }
    };

    const handleModeToggle = (nextIsJson: boolean) => {
        if (nextIsJson) {
            setMode('json');
        } else {
            showForm();
        }
    };

    const formTitle = schema.title || '';

    return (
        <div className="app-sf-section">
            <div className="app-sf-section-header">
                <div className="app-sf-section-controls">
                    <IconButton
                        aria-label={collapsed ? 'Expand section' : 'Collapse section'}
                        onClick={() => setCollapsed((c) => !c)}
                        size="small"
                    >
                        {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={mode === 'json'}
                                onChange={(e) => handleModeToggle(e.target.checked)}
                                inputProps={{ 'aria-label': 'Toggle form / JSON' }}
                            />
                        }
                        label={mode === 'json' ? 'Raw JSON' : 'Form'}
                    />
                    {onStatusChange && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={attrStatus === 'ACTIVE'}
                                    onChange={(e) => onStatusChange(e.target.checked ? 'ACTIVE' : 'INACTIVE')}
                                    inputProps={{ 'aria-label': 'Toggle ACTIVE / INACTIVE' }}
                                />
                            }
                            label={attrStatus}
                        />
                    )}
                </div>
                <div className="app-sf-section-title">{formTitle}</div>
            </div>
            {!collapsed && (
                <div className="app-sf-section-body">
                    {mode === 'form' ? (
                        <AppSchemaForm
                            schema={schema}
                            values={values}
                            onChange={onChange}
                            extraErrorsByPointer={extraErrorsByPointer}
                            imagePreviewMap={imagePreviewMap}
                        />
                    ) : (
                        <RawJsonEditor
                            text={draft.text}
                            error={draft.error}
                            onTextChange={draft.setText}
                            onBlur={draft.commit}
                            actions={(
                                <Button variant="outlined" onClick={showForm}>
                                    Apply JSON & Show Form
                                </Button>
                            )}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
